import { ChangeDetectionStrategy, Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { sortArrayByAttribute } from '../helpers';
import { AntragDtoModel, AntragsInfoExtended } from '../models';
import { ConfigSettingsService, MessageBase, ResourceProvider } from '../providers';
import { AntragHelperService } from './antrag-helper.service';

@Component({
  selector: 'ebeihilfe-antrags-overview',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class AntragsOverviewBaseComponent implements OnInit {
  public antragsList: WritableSignal<AntragsInfoExtended[]> = signal<AntragsInfoExtended[]>([]);
  public antragsListV2: WritableSignal<AntragsInfoExtended[]> = signal<AntragsInfoExtended[]>([]);
  public drafts: WritableSignal<AntragsInfoExtended[]> = signal<AntragsInfoExtended[]>([]);
  public einreichungV3Activated = signal(false);
  public isLoadingAntrage = signal(false);
  public orderByArray: { value: keyof AntragsInfoExtended; text: string }[] | undefined;

  protected readonly antragHelper = inject(AntragHelperService);
  protected readonly configSettingsService = inject(ConfigSettingsService);
  protected readonly message = inject(MessageBase);
  protected readonly resource = inject(ResourceProvider);
  protected readonly translate = inject(TranslateService);

  public async loadAntraege(): Promise<void> {
    return this.loadAntraegeInternal();
  }

  public ngOnInit(): void {
    this.einreichungV3Activated.set(!!this.configSettingsService.appSettings.einreichungV3Activated);
    this.loadAntraege();

    this.orderByArray = [
      {
        value: 'hochgeladen',
        text: this.translate.instant(_('bescheid_list.label.orderBy.angelegt_am')),
      },
      {
        value: 'anzBelege',
        text: this.translate.instant(_('bescheid_list.label.orderBy.anzahl_belege')),
      },
      {
        value: 'anzSeiten',
        text: this.translate.instant(_('bescheid_list.label.orderBy.anzahl_seiten')),
      },
      {
        value: 'status',
        text: this.translate.instant(_('bescheid_list.label.orderBy.status')),
      },
      {
        value: 'seitenBytesTotal',
        text: this.translate.instant(_('bescheid_list.label.orderBy.groesse')),
      },
    ];
  }

  public refresh(): Promise<void> {
    return this.loadAntraege();
  }

  public sortAntragsList(nameOfAttribute: keyof AntragsInfoExtended): void {
    const sortByAttributeType = nameOfAttribute === 'hochgeladen' ? 'date' : 'number';
    sortArrayByAttribute(this.antragsList(), nameOfAttribute, sortByAttributeType);
  }

  protected abstract hideSpinner(): void | Promise<boolean>;

  protected abstract showSpinner(): void | Promise<void>;

  protected async loadAntraegeInternal(ratingFn?: () => Promise<void>): Promise<void> {
    if (this.isLoadingAntrage()) {
      return;
    }

    this.startLoading();

    try {
      const antrage = await this.loadAntragsHistory();
      if (antrage.length > 2 && !!ratingFn) {
        await ratingFn();
      }

      const infoList = await this.convertIntoExtendedAntragsInfo(antrage);
      this.updateAntragsLists(infoList);
    } catch (err: unknown) {
      await this.handleLoadError(err);
    } finally {
      this.finishLoading();
    }
  }

  private async convertIntoExtendedAntragsInfo(antrage: AntragDtoModel[]): Promise<AntragsInfoExtended[]> {
    const filteredAntraege = antrage.filter(
      (antrag) => !this.antragHelper.filterForDifferentEinreichungsVersions(antrag),
    );

    return await Promise.all(
      filteredAntraege.map(async (antrag) => await this.antragHelper.turnIntoAntragsInfoExtended(antrag)),
    );
  }

  private startLoading(): void {
    this.isLoadingAntrage.set(true);
    this.showSpinner();
  }

  private finishLoading(): void {
    this.hideSpinner();
    this.isLoadingAntrage.set(false);
  }

  private async loadAntragsHistory(): Promise<AntragDtoModel[]> {
    return firstValueFrom(this.resource.getAntragsHistory(true));
  }

  private updateAntragsLists(infoList: AntragsInfoExtended[]): void {
    const completed = this.getCompletedAntraege(infoList);
    const drafts = this.getDraftAntraege(infoList);

    this.antragsList.set(completed);
    this.drafts.set(drafts);

    if (!this.isEinreichungV3Activated()) {
      this.antragsListV2.set([...drafts, ...completed]);
    }
  }

  private getCompletedAntraege(infoList: AntragsInfoExtended[]): AntragsInfoExtended[] {
    return sortArrayByAttribute(
      infoList.filter((antrag) => antrag.hochgeladen !== null),
      'hochgeladen',
      'date',
    );
  }

  private getDraftAntraege(infoList: AntragsInfoExtended[]): AntragsInfoExtended[] {
    return sortArrayByAttribute(
      infoList.filter((antrag) => antrag.hochgeladen === null),
      'angelegt',
      'date',
    );
  }

  private isEinreichungV3Activated(): boolean {
    return this.configSettingsService.appSettings.einreichungV3Activated === true;
  }

  private async handleLoadError(err: unknown): Promise<void> {
    if (err instanceof Error) {
      await this.message.showError(err);
    }
  }
}
